import React, { useState } from "react"
import { Space, Flex, Divider, Button, Tag, Row, Col } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { Text } from "ui/Typography"
import PropTypes from "prop-types"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import { colors } from "themes/variables"
import { createCardHold, captureCardHold, releaseCardHold, getClientBookingDetails } from "store/bookings"
import { toaster } from "components/common/Toast"
import styles from "./index.module.css"
import PlaceHoldModal from "./Modals/PlaceHold"
import ManageHoldModal from "./Modals/ManageHold"

const statusColors = {
  active: "success",
  voided: "default",
  expired: "warning",
  captured: "processing"
}

const mapHoldItem = (hold) => ({
  id: hold.id,
  amount: hold.amount,
  card_brand: hold.method,
  last4: hold.last4,
  status: hold.hold_state ?? "active",
  expires_at: hold.expires_at,
  expiration_message: hold.expiration_message,
  is_expiring_soon: hold.is_expiring_soon,
  expires_in_days: hold.expires_in_days,
  can_capture: hold.can_capture,
  can_release: hold.can_release,
  payment_intent_id: hold.payment_intent_id,
  authorized_on: hold.authorized_on,
  captured_amount: hold.captured_amount,
  original_amount: hold.original_amount,
  partial_capture: hold.partial_capture,
  captured_at: hold.captured_at
})

const CardHold = ({ holdHistory = [], bookingUUID, bookingId, clientId, onRefresh }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isPlaceHoldOpen, setIsPlaceHoldOpen] = useState(false)
  const [selectedHold, setSelectedHold] = useState(null)

  const holds = holdHistory.map(mapHoldItem)

  // The new reservation detail page passes onRefresh to reload through its own detail fetch;
  // the old booking detail omits it and falls back to reloading the client booking payload.
  const refreshDetail = () => (onRefresh ? onRefresh() : dispatch(getClientBookingDetails(bookingUUID)))

  const handlePlaceHold = ({ payment_method_id, amount } = {}) => {
    dispatch(
      createCardHold(bookingId, {
        authorization: {
          hold_amount: amount,
          payment_method_id,
          payment_provider: "stripe"
        }
      })
    )
      .then(() => {
        refreshDetail()
        setIsPlaceHoldOpen(false)
      })
      .catch(() => {
        toaster.error(t("client.payment.card_hold.place_hold_modal.failed"))
      })
  }

  const handleCloseManage = () => setSelectedHold(null)

  const handleChargeHold = (holdId, data) => {
    dispatch(captureCardHold(bookingId, holdId, data))
      .then(() => {
        refreshDetail()
        handleCloseManage()
      })
      .catch(() => {
        toaster.error(t("client.payment.card_hold.manage_hold_modal.charge_view.failed"))
      })
  }

  const handleVoidHold = (holdId) => {
    dispatch(releaseCardHold(bookingId, holdId))
      .then(() => {
        refreshDetail()
        handleCloseManage()
      })
      .catch(() => {
        toaster.error(t("client.payment.card_hold.manage_hold_modal.remove_view.failed"))
      })
  }

  return (
    <>
      <Space direction="vertical" size={8} className={`${styles.cardHoldContainer} w-full`}>
        <Divider size="small" />
        <Flex align="center" justify="space-between">
          <span className={styles.sectionTitle}>{t("client.payment.card_hold.section_title")}</span>
          <Button type="link" size="small" className={`p-0 ${styles.placeHoldBtn}`} icon={<PlusOutlined />} onClick={() => setIsPlaceHoldOpen(true)}>
            {t("client.payment.card_hold.place_hold")}
          </Button>
        </Flex>

        {holds?.length === 0 ? (
          <Text color={colors.textSecondary}>{t("client.payment.card_hold.no_holds")}</Text>
        ) : (
          <Flex vertical>
            {holds.map((hold, idx) => {
              const { id, amount, card_brand, last4, status, expiration_message, can_capture, can_release, partial_capture, original_amount, captured_amount } = hold
              const tagColor = statusColors[status] ?? statusColors.active
              const tagLabel = t(`client.payment.card_hold.status.${status}`, { defaultValue: status })
              const isActionable = can_capture || can_release
              const isCaptured = status === "captured"

              return (
                <React.Fragment key={id ?? idx}>
                  <Row gutter={8} className="py-xs" align="middle">
                    <Col xs={14}>
                      <Flex vertical gap={2}>
                        <Text fw={500}>{numberToCurrency(isCaptured ? captured_amount : amount)}</Text>
                        <Text color={colors.textSecondaryDark}>
                          {[card_brand, last4 ? `**** ${last4}` : null].filter(Boolean).join(" ")}
                        </Text>
                        {isCaptured && partial_capture && (
                          <Text color={colors.textSecondaryDark} style={{ fontSize: 12 }}>
                            {t("client.payment.card_hold.partial_capture_note", { original: numberToCurrency(original_amount) })}
                          </Text>
                        )}
                        {!isCaptured && expiration_message && (
                          <Text color={colors.warning} style={{ fontSize: 12 }}>
                            {expiration_message}
                          </Text>
                        )}
                      </Flex>
                    </Col>
                    <Col xs={10}>
                      <Flex align="center" justify="flex-end" gap={8}>
                        <Tag color={tagColor}>{tagLabel}</Tag>
                        {isActionable && (
                          <Button type="link" className="p-0" size="small" onClick={() => setSelectedHold(hold)}>
                            {t("client.payment.card_hold.edit")}
                          </Button>
                        )}
                      </Flex>
                    </Col>
                  </Row>
                  {idx < holds.length - 1 && <Divider size="small" />}
                </React.Fragment>
              )
            })}
          </Flex>
        )}
      </Space>

      {isPlaceHoldOpen && (
        <PlaceHoldModal
          open={isPlaceHoldOpen}
          onClose={() => setIsPlaceHoldOpen(false)}
          onSubmit={handlePlaceHold}
          clientId={clientId}
          bookingUUID={bookingUUID}
        />
      )}

      {selectedHold && (
        <ManageHoldModal
          open={!!selectedHold}
          hold={selectedHold}
          onClose={handleCloseManage}
          onCharge={handleChargeHold}
          onVoid={handleVoidHold}
        />
      )}
    </>
  )
}

CardHold.propTypes = {
  holdHistory: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      amount: PropTypes.number,
      method: PropTypes.string,
      last4: PropTypes.string,
      status: PropTypes.string,
      authorized_on: PropTypes.string,
      can_capture: PropTypes.bool,
      can_release: PropTypes.bool,
      payment_intent_id: PropTypes.string
    })
  ),
  bookingUUID: PropTypes.string,
  bookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRefresh: PropTypes.func
}

export default CardHold
